import { createContext, useContext, useState, useEffect, useCallback } from "react";
import api from "../lib/api";
import wsService from "../lib/websocket";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  // Initialize user from localStorage for faster hydration (iOS Safari)
  const [user, setUser] = useState(() => {
    try {
      const savedUser = localStorage.getItem('user_data');
      return savedUser ? JSON.parse(savedUser) : null;
    } catch {
      return null;
    }
  });
  const [loading, setLoading] = useState(true);
  const [wsConnected, setWsConnected] = useState(false);

  const checkAuth = async () => {
    const token = localStorage.getItem('auth_token');
    
    // If no token, clear user and stop loading
    if (!token) {
      setUser(null);
      localStorage.removeItem('user_data');
      setLoading(false);
      return;
    }
    
    try {
      const response = await api.getMe();
      setUser(response.data);
      localStorage.setItem('user_data', JSON.stringify(response.data));
      
      // Connect WebSocket after auth check
      wsService.connect(token);
      wsService.startPing();
    } catch (error) {
      setUser(null);
      localStorage.removeItem('auth_token');
      localStorage.removeItem('user_data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    checkAuth();
    
    // WebSocket connection status listeners
    const unsubConnected = wsService.on('connected', () => {
      setWsConnected(true);
    });
    
    const unsubDisconnected = wsService.on('disconnected', () => {
      setWsConnected(false);
    });
    
    return () => {
      unsubConnected();
      unsubDisconnected();
      wsService.stopPing();
      wsService.disconnect();
    };
  }, []);

  const login = useCallback((userData, token) => {
    setUser(userData);
    if (token) {
      localStorage.setItem('auth_token', token);
      localStorage.setItem('user_data', JSON.stringify(userData));
      wsService.connect(token);
      wsService.startPing();
    }
  }, []);

  const logout = async () => {
    try {
      await api.logout();
    } catch (error) {
      console.error("Logout error:", error);
    }
    setUser(null);
    localStorage.removeItem('auth_token');
    localStorage.removeItem('user_data');
    wsService.disconnect();
  };

  return (
    <AuthContext.Provider value={{ 
      user, 
      loading, 
      login, 
      logout, 
      checkAuth, 
      setUser,
      wsConnected,
      wsService 
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return context;
}
